import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  AdvancedMarker,
  APIProvider,
  Map,
  InfoWindow,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { db, auth, analytics } from "./FirebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import { API_KEY, MAP_GIVEN_ID } from "./constants";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { signInAnonymously } from "firebase/auth";
import { logEvent } from "firebase/analytics";
import { RxHamburgerMenu } from "react-icons/rx";
import logo_image_bg_black from "./Components/CSG_Logo_A.jpg";

import SimpleCarousel from "./Components/CustomCarousel";

// Component to activate map animations on initial load
const MapAnimationActivator = () => {
  const map = useMap();
  const hasActivated = useRef(false);

  useEffect(() => {
    if (!map || hasActivated.current) return;

    // Wait for map to be fully ready, then zoom in by 1 level
    const activateAnimations = () => {
      const currentZoom = map.getZoom();
      if (currentZoom && currentZoom > 0 && !hasActivated.current) {
        // Zoom in by 1 level to activate animation system
        map.setZoom(currentZoom + 1);
        hasActivated.current = true;
        //console.log("✅ Map animations activated with initial zoom");
      }
    };

    // Try immediately, or wait a bit if map isn't ready
    if (map.getZoom() && map.getZoom()! > 0) {
      setTimeout(activateAnimations, 100);
    } else {
      // Wait for map to be ready
      setTimeout(activateAnimations, 500);
    }
  }, [map]);

  return null;
};

// Clustering component that can access the map instance
const ClusteredMarkers = ({ 
  carouselGroups, 
  isLoading, 
  onMarkerClick 
}: { 
  carouselGroups: CarouselPoints[], 
  isLoading: boolean, 
  onMarkerClick: (group: CarouselPoints) => void 
}) => {
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markerDataRef = useRef<string>(''); // Track marker data to prevent unnecessary recreation
  const carouselGroupsRef = useRef<CarouselPoints[]>(carouselGroups); // Store latest for click handler

  // Update ref when carouselGroups changes (for click handler)
  useEffect(() => {
    carouselGroupsRef.current = carouselGroups;
  }, [carouselGroups]);

  useEffect(() => {
    if (!map || isLoading || carouselGroups.length === 0) return;

    // Create a stable key from essential marker data (excludes images)
    const currentMarkerKey = carouselGroups.map(g => `${g.id}-${g.lat}-${g.lon}-${g.number}-${g.pinColor}`).join('|');
    
    // Only skip recreation if we already have markers with the same key AND clusterer exists
    // This prevents recreation when only images load, but allows initial creation
    // IMPORTANT: Check this BEFORE any cleanup to prevent markers from being cleared
    const hasExistingMarkers = markerDataRef.current === currentMarkerKey && clustererRef.current !== null && markerDataRef.current !== '';
    
    // console.log("🔍 Marker recreation check:", {
    //   previousKey: markerDataRef.current || '(none)',
    //   currentKey: currentMarkerKey.substring(0, 50) + '...',
    //   keysMatch: markerDataRef.current === currentMarkerKey,
    //   hasClusterer: clustererRef.current !== null,
    //   hasPreviousKey: markerDataRef.current !== '',
    //   shouldSkip: hasExistingMarkers
    // });
    
    if (hasExistingMarkers) {
      // console.log("⏭️ Skipping marker recreation - only images changed");
      return; // Skip recreation if only images changed - cleanup won't run because we return early
    }
    
    // console.log("🔄 Creating/updating markers. Previous key:", markerDataRef.current || '(none)', "New key:", currentMarkerKey.substring(0, 50) + '...', "Groups count:", carouselGroups.length);
    markerDataRef.current = currentMarkerKey;

    // Add a small delay to ensure marker library is loaded
    const timer = setTimeout(() => {
      // Check if Google Maps marker library is loaded
      if (!google.maps.marker || !google.maps.marker.PinElement) {
        // console.error("❌ Google Maps marker library not loaded");
        // console.error("Available:", { 
        //   maps: !!google.maps, 
        //   marker: !!google.maps.marker,
        //   PinElement: !!(google.maps.marker && google.maps.marker.PinElement)
        // });
        return;
      }
      
      // Clear existing markers AFTER confirming library is loaded (only if we're actually recreating)
      if (clustererRef.current) {
        // console.log("🧹 Clearing existing markers before recreation");
        clustererRef.current.clearMarkers();
      }

      // console.log("✅ Google Maps marker library loaded, creating markers...");

    // Create AdvancedMarkerElement markers for clustering
    const markers = carouselGroups.map((group) => {
      const position = {
        lat: parseFloat(group.lat),
        lng: parseFloat(group.lon),
      };

      // Ensure pinColor has # prefix
      const pinColor = group.pinColor 
        ? (group.pinColor.startsWith('#') ? group.pinColor : `#${group.pinColor}`)
        : "#2563eb";

      // Special debugging for hotel markers
      // if (group.title === "Hotel") {
      //   console.log(`🏨 Creating hotel marker for:`, group.title);
      //   console.log(`🏨 Hotel position:`, position);
      //   console.log(`🏨 Hotel points count:`, group.points.length);
      //   console.log(`🏨 Hotel coordinates valid:`, !isNaN(position.lat) && !isNaN(position.lng));
      //   console.log(`🏨 Hotel pinColor:`, pinColor);
      //   console.log(`🏨 Hotel number:`, group.number);
      // }

      try {
        // Create custom pin with gradient background
        const createGradientPin = (color: string, number: string) => {
          const pinContainer = document.createElement('div');
          pinContainer.style.position = 'relative';
          pinContainer.style.width = '36px';
          pinContainer.style.height = '36px';
          pinContainer.style.borderRadius = '60% 60% 60% 0%';
          pinContainer.style.transform = 'rotate(-45deg)';
          pinContainer.style.border = '1px solid #3B3A3A';
          pinContainer.style.cursor = 'pointer';
          
          // Create gradient background
          const gradient = document.createElement('div');
          gradient.style.position = 'absolute';
          gradient.style.top = '0';
          gradient.style.left = '0';
          gradient.style.width = '100%';
          gradient.style.height = '100%';
          gradient.style.borderRadius = '60% 60% 60% 0%';
          
          // Create gradient from top-left to bottom-right
          const lighterColor = lightenColor(color, 0.5);
          gradient.style.background = `linear-gradient(135deg, ${color} 0%, ${lighterColor} 100%)`;
          
          // Add number text
          const numberText = document.createElement('div');
          numberText.textContent = number;
          numberText.style.position = 'absolute';
          numberText.style.top = '50%';
          numberText.style.left = '50%';
          numberText.style.transform = 'translate(-50%, -50%) rotate(45deg)';
          numberText.style.color = '#FFFFFF';
          numberText.style.fontSize = '12px';
          numberText.style.fontWeight = 'bold';
          numberText.style.textAlign = 'center';
          numberText.style.lineHeight = '1';
          numberText.style.pointerEvents = 'none';
          
          pinContainer.appendChild(gradient);
          pinContainer.appendChild(numberText);
          
          return pinContainer;
        };

        // Helper function to lighten colors
        const lightenColor = (color: string, factor: number) => {
          const hex = color.replace('#', '');
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          
          const newR = Math.min(255, Math.floor(r + (255 - r) * factor));
          const newG = Math.min(255, Math.floor(g + (255 - g) * factor));
          const newB = Math.min(255, Math.floor(b + (255 - b) * factor));
          
          return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        };

        // Create custom gradient pin
        const customPinElement = createGradientPin(pinColor, group.number);

        // Create AdvancedMarkerElement with custom gradient pin
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          content: customPinElement,
          title: group.title,
          map: null, // Don't add to map directly - let clusterer handle it
        });

        // Add click listener with smooth transition
        marker.addListener("click", () => {
          // Get the latest group data (with images) from ref
          const latestGroup = carouselGroupsRef.current.find(g => g.id === group.id) || group;
          // console.log("🖱️ Marker clicked:", latestGroup.title);
          
          // Smooth transition to marker position
          const markerPosition = {
            lat: parseFloat(latestGroup.lat),
            lng: parseFloat(latestGroup.lon),
          };
          
          // Pan to marker with smooth animation
          map.panTo(markerPosition);
          
          // Zoom to the marker for better focus
          setTimeout(() => {
            map.setZoom(18); // Zoom level for detailed view
          }, 200);
          
          // Small delay to ensure smooth transition before opening InfoWindow
          setTimeout(() => {
            onMarkerClick(latestGroup);
          }, 500);
        });

        return marker;
      } catch (error) {
        // console.error(`❌ Error creating marker for ${group.title}:`, error);
        return null;
      }
    }).filter(marker => marker !== null);

    // console.log(`📍 Created ${markers.length} markers from ${carouselGroups.length} groups`);

    // Create new clusterer with default configuration
    const clusterer = new MarkerClusterer({
      map: map,
      markers,
    });
    
    // console.log(`✅ Clusterer created with ${markers.length} markers`);

    // Add smooth transition for cluster clicks with a small delay
    setTimeout(() => {
      clusterer.addListener("click", (event: any) => {
        // console.log("🖱️ Cluster clicked:", event);
        
        // Check if map is ready and has proper zoom level
        const currentZoom = map.getZoom();
        if (!currentZoom || currentZoom < 1) {
          // console.log("⚠️ Map not ready for cluster interaction");
          return;
        }
        
        // Get cluster bounds for smooth zoom
        if (event.latLng) {
          const clusterPosition = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };
          
          // console.log("🔍 Current zoom level:", currentZoom);
          // console.log("📍 Cluster position:", clusterPosition);
          
          // Calculate target zoom level with maximum limit
          const MAX_ZOOM_LEVEL = 15; // Maximum zoom level allowed
          let targetZoom = currentZoom;
          
          if (currentZoom < 14) {
            targetZoom = Math.min(currentZoom + 2, MAX_ZOOM_LEVEL);
          } else if (currentZoom < MAX_ZOOM_LEVEL) {
            targetZoom = Math.min(currentZoom + 1, MAX_ZOOM_LEVEL);
          } else {
            // Already at or above max zoom, no zoom change
            targetZoom = currentZoom;
          }
          
          // Prevent default cluster zoom behavior
          event.stopPropagation();
          
          // Use smooth pan and zoom with proper timing
          if (targetZoom > currentZoom) {
            // console.log("🔍 Smooth zoom from", currentZoom, "to", targetZoom);
            // if (targetZoom === MAX_ZOOM_LEVEL && currentZoom < MAX_ZOOM_LEVEL) {
            //   console.log("⚠️ Zoom limited to maximum level:", MAX_ZOOM_LEVEL);
            // }
            
            // Smooth pan and zoom (animations should be activated on map load)
            map.panTo(clusterPosition);
            setTimeout(() => {
              map.setZoom(targetZoom);
            }, 300);
          } else {
            // Just pan if no zoom needed
            // console.log("🔍 No zoom change needed - already at max level or no zoom required");
            map.panTo(clusterPosition);
          }
        }
      });
    }, 200); // Small delay to ensure clusterer is ready

    clustererRef.current = clusterer;

    }, 100); // 100ms delay to ensure library is loaded

    // Cleanup function - only clear timeout, don't clear markers
    // Markers are cleared above before creating new ones, so we don't need to clear here
    return () => {
      // console.log("🧹 Cleanup running - clearing timeout only");
      clearTimeout(timer);
      // Don't clear markers here - they're only cleared when we're actually recreating them
    };
  }, [map, carouselGroups, isLoading, onMarkerClick]);

  return null; // This component doesn't render anything
};

export type SinglePoints = {
  name: string;
  description: string;
  gmapsLink?: string;
  webLink?: string;
  imageLink?: string;
};

export type CarouselPoints = {
  id: string;
  title: string;
  lat: string;
  lon: string;
  number: string;
  pinColor?: string;
  points: SinglePoints[];
};

function MyMaps() {
  const [isMobile, setIsMobile] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // For the custom carousel:
  const [carouselGroups, setCarouselGroups] = useState<CarouselPoints[]>([]);
  const [rawCarouselData, setRawCarouselData] = useState<CarouselPoints[]>([]); // Data without images
  const [selectedCarousel, setSelectedCarousel] =
    useState<CarouselPoints | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // For the dropdown list
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [selectedPointIndex, setSelectedPointIndex] = useState<number>(0);

  // Handler to reset index when clicking a pin
  const handlePinClick = (group: CarouselPoints) => {
    setSelectedPointIndex(0); // Always reset to first page
    setSelectedCarousel(group);
  };

  // Memoized marker data (only essential fields, excludes images to prevent marker recreation)
  const markerDataKey = useMemo(() => {
    return carouselGroups.map(g => `${g.id}-${g.lat}-${g.lon}-${g.number}-${g.pinColor}`).join('|');
  }, [carouselGroups.length, carouselGroups.map(g => `${g.id}-${g.lat}-${g.lon}-${g.number}-${g.pinColor}`).join('|')]);
  
  const markerData = useMemo(() => {
    return carouselGroups.map(group => ({
      id: group.id,
      title: group.title,
      lat: group.lat,
      lon: group.lon,
      number: group.number,
      pinColor: group.pinColor,
      points: group.points, // Keep points for click handler
    }));
  }, [markerDataKey]);

  // Debug logging for carousel groups
  useEffect(() => {
    // console.log("🗺️ Rendering markers for groups:", carouselGroups.length, carouselGroups);
  }, [carouselGroups]);

  // QR Code / URL Parameter handling
  useEffect(() => {
    if (carouselGroups.length === 0) return; // Wait for data to load

    const urlParams = new URLSearchParams(window.location.search);
    const category = urlParams.get('category');
    const name = urlParams.get('name');

    // console.log("🔍 URL Parameters:", { category, name });

    // If only name is provided, search across all categories
    if (name && !category) {
      // console.log("🔍 Searching for point by name only:", name);
      
      // Search through all groups to find the point
      for (const group of carouselGroups) {
        const pointIndex = group.points.findIndex(point => 
          point.name.toLowerCase().replace(/\s+/g, '') === name.toLowerCase() ||
          point.name.toLowerCase().replace(/\s+/g, '-') === name.toLowerCase()
        );
        
        if (pointIndex !== -1) {
          // console.log("🎯 Found point in category:", group.title);
          
          // Open the specific point directly (no dropdown)
          setSelectedPointIndex(pointIndex);
          setSelectedCarousel(group);
          // console.log("🎯 Opening specific point by name:", group.points[pointIndex].name);
          return; // Exit early once found
        }
      }
      
      // console.log("❌ Point not found in any category:", name);
    }
    // If category is provided (with or without name)
    else if (category) {
      // Find the category in our groups
      const targetGroup = carouselGroups.find(group => 
        group.title.toLowerCase().replace(/\s+/g, '-') === category.toLowerCase() ||
        group.title.toLowerCase().replace(/\s+/g, '') === category.toLowerCase()
      );

      if (targetGroup) {
        // console.log("🎯 Found target group:", targetGroup.title);
        
        // If specific point name requested
        if (name) {
          const pointIndex = targetGroup.points.findIndex(point => 
            point.name.toLowerCase().replace(/\s+/g, '') === name.toLowerCase() ||
            point.name.toLowerCase().replace(/\s+/g, '-') === name.toLowerCase()
          );
          
          if (pointIndex !== -1) {
            setSelectedPointIndex(pointIndex);
            setSelectedCarousel(targetGroup);
            // console.log("🎯 Opening specific point by name:", targetGroup.points[pointIndex].name);
          } else {
            // console.log("❌ Point not found:", name);
            // Fallback to first point
            setSelectedPointIndex(0);
            setSelectedCarousel(targetGroup);
          }
        } else {
          // Open first point of the category
          setSelectedPointIndex(0);
          setSelectedCarousel(targetGroup);
          // console.log("🎯 Opening first point of category");
        }
      } else {
        // console.log("❌ Category not found:", category);
      }
    }
  }, [carouselGroups]);

  // Removed complex clustering - using simple AdvancedMarker approach

  useEffect(() => {
    const onResize = () => {
      setViewportHeight(window.innerHeight);
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (analytics) {
      logEvent(analytics, "page_view");
    }
  }, []);

  // Removed sample data - only show real Firestore data

  // First useEffect: Fetch Firestore data (fast - shows markers immediately)
  useEffect(() => {
    const fetchCarouselPoints = async () => {
      try {
        setIsLoading(true);
        await signInAnonymously(auth);
        // console.log("✅ Signed in anonymously");

        const querySnapshot = await getDocs(collection(db, "carouselPointsV5"));
        
        // console.log("📊 Raw Firestore data received:");
        // console.log("Number of documents:", querySnapshot.docs.length);
        
        if (querySnapshot.docs.length === 0) {
          // console.warn("⚠️ No documents found in collection 'carouselPoints'. Please check the collection name.");
        }

        const data = await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            const d = doc.data();
            // console.log(`📄 Document ${doc.id}:`, d);
            
            let rawPoints = d.points;
            // console.log(`🔍 Raw points for ${doc.id}:`, rawPoints, "Type:", typeof rawPoints);
            
            // Special debugging for hotel data
            if (doc.id === "Hotel" || d.title === "Hotel") {
              // console.log(`🏨 HOTEL DATA DEBUG:`);
              // console.log(`🏨 Document ID:`, doc.id);
              // console.log(`🏨 Title:`, d.title);
              // console.log(`🏨 Raw points type:`, typeof rawPoints);
              // console.log(`🏨 Raw points length:`, rawPoints ? rawPoints.length : 'undefined');
              // console.log(`🏨 Raw points preview:`, rawPoints && typeof rawPoints === 'string' ? rawPoints.substring(0, 100) + '...' : 'undefined');
            }

            // Parse stringified JSON if needed
            if (typeof rawPoints === "string") {
              try {
                let cleanedJson = rawPoints
                  .replace(/[""''""]/g, '"')
                  .replace(/[''']/g, "'")
                  .replace(/\s+/g, ' ')
                  .trim();

                rawPoints = JSON.parse(cleanedJson);
                // console.log(`✅ Parsed points for ${doc.id}:`, rawPoints);
                // console.log(`📊 Points count for ${doc.id}:`, rawPoints.length);
              } catch (error) {
                // console.warn(`❌ Failed to parse points for doc ${doc.id}:`, rawPoints, error);
                // console.warn(`❌ Raw points string:`, typeof rawPoints === 'string' ? rawPoints.substring(0, 200) + '...' : 'Not a string');
                
                if (typeof rawPoints === 'string') {
                  try {
                    let fixedJson = rawPoints
                      .replace(/[""''""]/g, '"')
                      .replace(/[''']/g, "'")
                      .replace(/,(\s*[}\]])/g, '$1')
                      .replace(/([{,]\s*)(\w+):/g, '$1"$2":')
                      .replace(/:(\s*)([^",{\[\s][^,}\]\]]*?)(\s*[,}\]])/g, ': "$2"$3');
                  
                    rawPoints = JSON.parse(fixedJson);
                    // console.log(`✅ Fixed and parsed points for ${doc.id}:`, rawPoints);
                  } catch (secondError) {
                    // console.error(`❌ Could not fix JSON for ${doc.id}:`, secondError);
                    rawPoints = [];
                  }
                } else {
                  // console.warn(`❌ Raw points is not a string for ${doc.id}, cannot parse as JSON`);
                  rawPoints = [];
                }
              }
            }

            // Create points WITHOUT loading images yet (for faster initial load)
            const points: SinglePoints[] = (rawPoints || []).map((pt: any) => ({
              name: pt.name,
              description: pt.description,
              gmapsLink: pt.gmapsLink,
              webLink: pt.webLink,
              imageLink: pt.imageLink || "", // Store the path, will load URL in second useEffect
            }));

            const parsedData = {
              id: doc.id,
              title: d.title,
              lat: d.lat,
              lon: d.lon,
              number: d.number,
              pinColor: d.pinColor,
              points,
            } as CarouselPoints;

            // Comprehensive logging for the number field
            // console.log(`🔢 NUMBER FIELD DEBUG for ${doc.id}:`, {
            //   rawNumber: d.number,
            //   numberType: typeof d.number,
            //   numberValue: parsedData.number,
            //   numberTrimmed: d.number ? d.number.trim() : 'null/undefined',
            //   isEmpty: !d.number || d.number.trim() === '',
            //   fullData: parsedData
            // });

            return parsedData;
          })
        );

        // Comprehensive logging of all retrieved data
        // console.log("=".repeat(80));
        // console.log("📊 COMPLETE DATABASE RETRIEVAL - ALL GROUPS:");
        // console.log("=".repeat(80));
        // console.log("Total groups:", data.length);
        // console.log("\n📋 DETAILED BREAKDOWN:");
        // data.forEach((group, index) => {
        //   console.log(`\n[${index + 1}] Group: "${group.title}" (ID: ${group.id})`);
        //   console.log(`   - Number: "${group.number}" (type: ${typeof group.number})`);
        //   console.log(`   - Number exists: ${!!group.number}`);
        //   console.log(`   - Number trimmed: "${group.number ? group.number.trim() : 'N/A'}"`);
        //   console.log(`   - Number length: ${group.number ? group.number.length : 0}`);
        //   console.log(`   - Pin Color: ${group.pinColor || 'default'}`);
        //   console.log(`   - Points count: ${group.points.length}`);
        //   console.log(`   - Location: (${group.lat}, ${group.lon})`);
        // });
        // console.log("\n📦 FULL DATA ARRAY:");
        // console.log(JSON.stringify(data, null, 2));
        // console.log("=".repeat(80));

        // Set data immediately (without images) so markers show up fast
        setRawCarouselData(data);
        setCarouselGroups(data); // Show markers immediately
        setIsLoading(false);
        // console.log("🎯 Initial carousel groups loaded (without images):", data);
      } catch (err) {
        // console.error("Error loading carousel data:", err);
        setIsLoading(false);
      }
    };

    fetchCarouselPoints();
  }, []);

  // Second useEffect: Load images from Storage (progressive - doesn't block UI)
  useEffect(() => {
    if (rawCarouselData.length === 0) return;

    const loadImages = async () => {
      const storage = getStorage();
      
      // Load images and update carouselGroups incrementally to avoid full refresh
      for (const group of rawCarouselData) {
        const pointsWithImages = await Promise.all(
          group.points.map(async (pt) => {
            let imageUrl = "";

            // Load image URL from Firebase Storage if imageLink path exists
            if (pt.imageLink) {
              try {
                const imageRef = ref(storage, decodeURIComponent(pt.imageLink));
                imageUrl = await getDownloadURL(imageRef);
              } catch (err) {
                // console.warn(`Failed to load image ${pt.imageLink}`, err);
                imageUrl = ""; // Keep empty if load fails
              }
            }

            return {
              ...pt,
              imageLink: imageUrl,
            };
          })
        );

        // Update this specific group's images without replacing entire array
        setCarouselGroups(prev => 
          prev.map(g => 
            g.id === group.id 
              ? { ...g, points: pointsWithImages }
              : g
          )
        );
      }

      // console.log("🖼️ Images loaded for all carousel groups");
    };

    loadImages();
  }, [rawCarouselData]);

  return (
    <div className="w-full relative" style={{ height: `${viewportHeight}px` }}>
      <APIProvider
        apiKey={API_KEY}
        libraries={["marker"]}
        onLoad={() => {/* console.log("🗺️ Maps API loaded successfully") */}}
      >
        <Map
          mapId={MAP_GIVEN_ID}
          defaultZoom={15}
          gestureHandling="greedy"
          defaultCenter={{ lat: 34.894733,  lng: 32.324955 }}
          streetViewControl={false}
          keyboardShortcuts={false}
          mapTypeId="satellite"
          zoomControl={!isMobile}
        >
          {/* Activate map animations on initial load */}
          <MapAnimationActivator />
          
          {/* Clustered markers component */}
          <ClusteredMarkers 
            carouselGroups={carouselGroups}
            isLoading={isLoading}
            onMarkerClick={handlePinClick}
          />

          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white/90 px-4 py-2 rounded-lg shadow-lg z-10">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm font-medium">Loading points...</span>
              </div>
            </div>
          )}

          {/* Points List Dropdown */}
          {!isLoading && carouselGroups.length > 0 && (
            <div className="absolute top-14 left-2 z-10">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="bg-transparent hover:bg-white/20 p-3 rounded-lg shadow-lg flex items-center justify-center transition-colors"
                title={`Points (${carouselGroups.length})`}
              >
                <RxHamburgerMenu className="w-6 h-6 text-gray-200" />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    {(() => {
                      // Sort all groups by number (don't group by title - show each group separately)
                      const sortedGroups = [...carouselGroups].sort((a, b) => {
                        const numA = parseInt(a.number) || 0;
                        const numB = parseInt(b.number) || 0;
                        return numA - numB;
                      });

                      return sortedGroups.map((group) => {
                        // Check if this group has only one point
                        const isSinglePoint = group.points.length === 1;
                        const displayNumber = group.number || '';
                        
                        // If single point, render just the point without header
                        if (isSinglePoint) {
                          const point = group.points[0];
                          
                          if (!point) return null;
                          
                          return (
                            <div key={group.id} className="mb-1">
                              <div
                                onClick={() => {
                                  setSelectedCarousel(group);
                                  setSelectedPointIndex(0);
                                  setIsDropdownOpen(false);
                                  setExpandedGroup(null);
                                }}
                                className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                              >
                                <div className="w-6 h-6 rounded-full border border-gray-700 bg-blue-100 flex items-center justify-center text-sm font-semibold text-gray-700">
                                  {displayNumber}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 truncate">{point.name}</div>
                                  <div className="text-sm text-gray-500 truncate">{point.description}</div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Otherwise, render normal header with expandable points
                        return (
                          <div key={group.id} className="mb-1">
                            {/* Group Header */}
                            <div
                              onClick={() => {
                                if (expandedGroup === group.id) {
                                  setExpandedGroup(null);
                                } else {
                                  setExpandedGroup(group.id);
                                }
                              }}
                              className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                            >
                                <div className="w-6 h-6 rounded-full border border-gray-700 bg-blue-100 flex items-center justify-center text-sm font-semibold text-gray-700">
                                  {displayNumber}
                                </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {group.title}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {group.points.length} {group.points.length === 1 ? 'place' : 'places'}
                                </div>
                              </div>
                              <svg 
                                className={`w-4 h-4 transition-transform ${expandedGroup === group.id ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          
                            {/* Individual Points - Show all points from this group */}
                            {expandedGroup === group.id && (
                              <div className="ml-4 mt-1 space-y-1">
                                {group.points.map((point, pointIndex) => (
                                  <div
                                    key={`${group.id}-${pointIndex}`}
                                    onClick={() => {
                                      setSelectedCarousel(group);
                                      setSelectedPointIndex(pointIndex);
                                      setIsDropdownOpen(false);
                                      setExpandedGroup(null);
                                    }}
                                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                                  >
                                    <div className="w-3 h-3 rounded-full bg-gray-300 flex items-center justify-center">
                                      <div className="w-1.5 h-1.5 rounded-full bg-gray-600"></div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-800 text-sm truncate">{point.name}</div>
                                      <div className="text-xs text-gray-500 truncate">{point.description}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show InfoWindow with carousel when group is selected */}
          {selectedCarousel && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedCarousel.lat),
                lng: parseFloat(selectedCarousel.lon),
              }}
              onCloseClick={() => {
                // Smooth transition when closing
                setTimeout(() => {
                  setSelectedCarousel(null);
                }, 100);
              }}
              pixelOffset={window.innerWidth <= 1000 ? [0, -25] : [0, -25]}
            >
              <div className="w-[300px] max-w-full">
                {/* Only show title if there are multiple points or title differs from point name */}
                {selectedCarousel.points.length > 1 || 
                 (selectedCarousel.points.length === 1 && 
                  selectedCarousel.title.trim().toLowerCase() !== selectedCarousel.points[0].name.trim().toLowerCase()) ? (
                  <h2 className="text-lg font-semibold mb-1">{selectedCarousel.title}</h2>
                ) : null}
                <SimpleCarousel points={selectedCarousel.points} initialIndex={selectedPointIndex} />
              </div>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>

      {/* Logo at bottom left */}
      <div className="absolute bottom-8 left-2 p-2 rounded-lg shadow-lg bg-black/50">
        <img
          src={logo_image_bg_black}
          alt="Logo"
          className="h-12 w-auto rounded-lg"
        />
      </div>
    </div>
  );
}

export default MyMaps;
